import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, LineStyle, IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import { Box, Paper, Typography, useTheme } from '@mui/material';

export interface TimelineDataPoint {
  time: string; // YYYY-MM-DD
  value: number;
}

interface TimelineChartProps {
  data: TimelineDataPoint[];
  title?: string;
  height?: number;
  baselineValue?: number; // e.g. 50 for 50% accuracy, or 0 for returns
  baselineLabel?: string;
  color?: string;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  data,
  title,
  height = 300,
  baselineValue,
  baselineLabel = 'Baseline',
  color = '#2962FF',
}) => {
  const theme = useTheme();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Use theme colors for dark mode compatibility
    const textColor = theme.palette.text.primary;
    const gridColor = theme.palette.divider;
    const borderColor = theme.palette.divider;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: textColor,
      },
      width: chartContainerRef.current.clientWidth,
      height: height,
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      rightPriceScale: {
        borderColor: borderColor,
      },
      timeScale: {
        borderColor: borderColor,
      },
      attributionLogo: false,
    } as any);

    chartRef.current = chart;

    const newSeries = chart.addSeries(LineSeries, {
      color: color,
      lineWidth: 2,
    });
    seriesRef.current = newSeries;

    // Set data
    // lightweight-charts expects distinct, ascending timestamps
    // We assume data is passed correctly, but sorting is safer
    const sortedData = [...data].sort((a, b) => 
      new Date(a.time).getTime() - new Date(b.time).getTime()
    );
    newSeries.setData(sortedData);

    // Add Baseline if provided
    if (baselineValue !== undefined) {
      newSeries.createPriceLine({
        price: baselineValue,
        color: '#9E9E9E',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: baselineLabel,
      });
    }

    // Fit content
    chart.timeScale().fitContent();

    // Resize handler
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [theme, height, baselineValue, baselineLabel, color]); // Re-create chart if theme/config changes

  // Effect to update data only without re-creating chart
  useEffect(() => {
    if (seriesRef.current && data) {
      const sortedData = [...data].sort((a, b) => 
        new Date(a.time).getTime() - new Date(b.time).getTime()
      );
      seriesRef.current.setData(sortedData);
    }
  }, [data]);

  return (
    <Paper elevation={1} sx={{ p: 2, width: '100%' }}>
      {title && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.length} data point{data.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
      <Box ref={chartContainerRef} sx={{ width: '100%', height }} />
    </Paper>
  );
};
